// =====================================================
// 白板协作 - 分享对话框（增强版）
// 功能：复制链接、在线协作者列表、权限区分
// =====================================================

import { useState } from 'react';
import { Copy, Check, X, Users, Link2, Loader2, ShieldCheck, Crown, Circle, UserMinus } from 'lucide-react';
import type { CollabUser, WhiteboardCollaboratorMember } from '../types';

interface ShareDialogProps {
  isOpen: boolean;
  onClose: () => void;
  isCollaborating: boolean;
  isOwner: boolean;
  /** 白板创建者的 userId，用于标注「所有者」 */
  createdBy: string | undefined;
  collaborators: Map<string, CollabUser>;
  shareInfo: { token: string; link: string } | null;
  onStartCollab: () => Promise<string | null>;
  onStopCollab: () => Promise<void>;
  members: WhiteboardCollaboratorMember[];
  onRemoveMember: (userId: string) => Promise<void>;
}

export function ShareDialog({
  isOpen,
  onClose,
  isCollaborating,
  isOwner,
  createdBy,
  collaborators,
  shareInfo,
  onStartCollab,
  onStopCollab,
  members,
  onRemoveMember,
}: ShareDialogProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [copied, setCopied] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [removingUserId, setRemovingUserId] = useState<string | null>(null);

  if (!isOpen) return null;

  const handleStartCollab = async () => {
    setIsLoading(true);
    try {
      await onStartCollab();
    } finally {
      setIsLoading(false);
    }
  };

  const handleStopCollab = async () => {
    setStopping(true);
    try {
      await onStopCollab();
    } finally {
      setStopping(false);
    }
  };

  const handleCopyLink = async () => {
    if (!shareInfo?.link) return;
    try {
      await navigator.clipboard.writeText(shareInfo.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      const textarea = document.createElement('textarea');
      textarea.value = shareInfo.link;
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  // 排序：自己排最前
  const collabList = [...collaborators.values()].sort((a, b) => {
    if (a.isCurrentUser) return -1;
    if (b.isCurrentUser) return 1;
    return 0;
  });
  const onlineCount = collabList.length;
  const persistentMembers = isOwner ? members : [];

  const handleRemoveMember = async (userId: string) => {
    setRemovingUserId(userId);
    try {
      await onRemoveMember(userId);
    } finally {
      setRemovingUserId(null);
    }
  };

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center" onClick={onClose}>
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" />
      <div
        className="relative bg-white rounded-2xl shadow-2xl w-[400px] max-h-[80vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200"
        onClick={(e) => e.stopPropagation()}
      >
        {/* 头部 */}
        <div className="px-5 pt-5 pb-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`w-8 h-8 rounded-xl flex items-center justify-center ${
              isCollaborating ? 'bg-green-100' : 'bg-blue-100'
            }`}>
              <Users className={`w-4 h-4 ${isCollaborating ? 'text-green-600' : 'text-blue-600'}`} />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-gray-900">
                {isCollaborating ? '协作中' : '分享白板'}
              </h3>
              {isCollaborating && (
                <p className="text-[11px] text-green-600">{onlineCount} 人在线</p>
              )}
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 transition-colors">
            <X className="w-4 h-4 text-gray-400" />
          </button>
        </div>

        {/* 内容 */}
        <div className="px-5 pb-5 overflow-y-auto max-h-[60vh]">
          {!isCollaborating ? (
            /* ===== 未开启协作 ===== */
            <>
              <p className="text-xs text-gray-500 mb-4 leading-relaxed">
                开启协作后，已登录的用户可以通过链接加入白板，实时查看和编辑内容。
              </p>
              <button
                onClick={handleStartCollab}
                disabled={isLoading}
                className="w-full h-10 bg-blue-500 text-white text-sm font-medium rounded-xl hover:bg-blue-600 transition-colors disabled:opacity-50 flex items-center justify-center gap-2"
              >
                {isLoading ? (
                  <><Loader2 className="w-4 h-4 animate-spin" /> 开启中...</>
                ) : (
                  <><Users className="w-4 h-4" /> 开启协作</>
                )}
              </button>
            </>
          ) : (
            /* ===== 已开启协作 ===== */
            <>
              {/* 协作链接 — 所有人可见 */}
              {shareInfo && (
                <div className="mb-4">
                  <label className="text-[11px] font-medium text-gray-500 mb-1.5 block">
                    协作链接 · 复制分享给其他人
                  </label>
                  <div className="flex gap-2">
                    <div className="flex-1 flex items-center gap-2 px-3 h-9 bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                      <Link2 className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span className="text-xs text-gray-600 truncate">{shareInfo.link}</span>
                    </div>
                    <button
                      onClick={handleCopyLink}
                      className={`shrink-0 h-9 px-3 text-xs font-medium rounded-lg transition-all flex items-center gap-1.5 ${
                        copied
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-500 text-white hover:bg-blue-600'
                      }`}
                    >
                      {copied
                        ? <><Check className="w-3.5 h-3.5" /> 已复制</>
                        : <><Copy className="w-3.5 h-3.5" /> 复制</>
                      }
                    </button>
                  </div>
                </div>
              )}

              {/* 在线协作者列表 */}
              {onlineCount > 0 && (
                <div className="mb-4">
                  <label className="text-[11px] font-medium text-gray-500 mb-2 block">
                    在线成员 ({onlineCount})
                  </label>
                  <div className="space-y-1.5">
                    {collabList.map((user) => {
                      const userIsOwner = createdBy ? user.userId === createdBy : false;
                      return (
                        <div
                          key={user.userId}
                          className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gray-50 hover:bg-gray-100 transition-colors"
                        >
                          {/* 头像 */}
                          <div className="relative">
                            <div
                              className="w-7 h-7 rounded-full flex items-center justify-center text-[11px] font-bold text-white shrink-0"
                              style={{ backgroundColor: user.color }}
                            >
                              {user.avatarUrl ? (
                                <img src={user.avatarUrl} alt={user.username} className="w-full h-full rounded-full object-cover" />
                              ) : (
                                user.username.charAt(0).toUpperCase()
                              )}
                            </div>
                            {/* 在线指示 */}
                            <Circle className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 fill-green-500 text-white stroke-[3]" />
                          </div>

                          {/* 名字 + 邮箱 */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1.5">
                              <span className="text-xs font-medium text-gray-800 truncate">
                                {user.username}
                              </span>
                              {user.isCurrentUser && (
                                <span className="text-[10px] text-gray-400 shrink-0">（我）</span>
                              )}
                            </div>
                            {user.email && (
                              <p className="text-[10px] text-gray-400 truncate mt-0.5">{user.email}</p>
                            )}
                          </div>

                          {/* 所有者标识 — 根据 createdBy 判断，不依赖 isCurrentUser */}
                          {userIsOwner && (
                            <span className="flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full shrink-0">
                              <Crown className="w-3 h-3" /> 所有者
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {isOwner && persistentMembers.length > 0 && (
                <div className="mb-4">
                  <label className="text-[11px] font-medium text-gray-500 mb-2 block">
                    已加入成员 ({persistentMembers.length})
                  </label>
                  <div className="space-y-1.5">
                    {persistentMembers.map((member) => {
                      const isOnline = collaborators.has(member.userId);
                      return (
                        <div key={member.userId} className="flex items-center gap-2.5 px-3 py-2 rounded-xl bg-gray-50">
                          <div className="relative w-7 h-7 rounded-full bg-slate-500 text-white text-[11px] font-bold flex items-center justify-center shrink-0">
                            {member.avatarUrl
                              ? <img src={member.avatarUrl} alt={member.name} className="w-full h-full rounded-full object-cover" />
                              : member.name.charAt(0).toUpperCase()}
                            {isOnline && <Circle className="w-2.5 h-2.5 absolute -bottom-0.5 -right-0.5 fill-green-500 text-white stroke-[3]" />}
                          </div>
                          <div className="flex-1 min-w-0">
                            <p className="text-xs font-medium text-gray-800 truncate">{member.name}</p>
                            <p className="text-[10px] text-gray-400 truncate">{member.email}</p>
                          </div>
                          {member.isOwner ? (
                            <span className="flex items-center gap-0.5 text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded-full">
                              <Crown className="w-3 h-3" /> 所有者
                            </span>
                          ) : (
                            <button
                              onClick={() => void handleRemoveMember(member.userId)}
                              disabled={removingUserId === member.userId}
                              className="p-1.5 rounded-lg text-red-500 hover:bg-red-50 disabled:opacity-50"
                              title="移除成员"
                            >
                              {removingUserId === member.userId
                                ? <Loader2 className="w-3.5 h-3.5 animate-spin" />
                                : <UserMinus className="w-3.5 h-3.5" />}
                            </button>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* 安全说明 */}
              <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-xl mb-4">
                <ShieldCheck className="w-4 h-4 text-gray-400 shrink-0 mt-0.5" />
                <div className="text-[11px] text-gray-500 leading-relaxed">
                  <p>仅已登录用户可通过此链接加入协作。</p>
                  <p className="mt-0.5">所有协作者均可复制链接邀请他人。</p>
                  {isOwner && <p className="mt-0.5">关闭协作后链接将立即失效。</p>}
                </div>
              </div>

              {/* 关闭协作 — 仅所有者可见 */}
              {isOwner && (
                <button
                  onClick={handleStopCollab}
                  disabled={stopping}
                  className="w-full h-9 border border-red-200 text-red-600 text-xs font-medium rounded-xl hover:bg-red-50 transition-colors disabled:opacity-50 flex items-center justify-center gap-1.5"
                >
                  {stopping ? (
                    <><Loader2 className="w-3.5 h-3.5 animate-spin" /> 关闭中...</>
                  ) : (
                    '关闭协作'
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
}
